import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, setToken, formatApiError } from "@/lib/admin";
import { API } from "@/lib/site";

const inputCls = "w-full border border-line bg-white px-4 py-2.5 text-sm text-ink outline-none focus:border-regal-amber transition-colors";

export default function AdminKess3() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = useCallback(() => {
    axios.get(`${API}/kess3-config`).then((r) => setCfg(r.data)).catch(() => toast.error("Failed to load KESS3 config"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setOpt = (list, i, k, v) =>
    setCfg((c) => ({ ...c, [list]: c[list].map((o, j) => (j === i ? { ...o, [k]: v } : o)) }));

  const save = async () => {
    setBusy(true);
    try {
      await adminApi().put("/admin/kess3-config", {
        hardware_price: parseFloat(cfg.hardware_price) || 0,
        currency: cfg.currency || "GBP",
        master_options: cfg.master_options.map((o) => ({ name: o.name, price: parseFloat(o.price) || 0 })),
        slave_options: cfg.slave_options.map((o) => ({ name: o.name, price: parseFloat(o.price) || 0 })),
      });
      toast.success("KESS3 options saved");
    } catch (e) {
      if (e.response?.status === 401) { setToken(null); nav("/admin/login"); }
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!cfg) return <main className="min-h-screen bg-surface pt-28"><div className="container-regal"><div className="h-96 animate-pulse border border-line bg-white" /></div></main>;

  const Section = ({ title, list }) => (
    <section className="border border-line bg-white p-6 sm:p-8" data-testid={`admin-kess3-${list}`}>
      <p className="overline-label mb-6">{title}</p>
      <div className="space-y-3">
        {cfg[list].map((o, i) => (
          <div key={i} className="flex items-center gap-3" data-testid={`admin-kess3-option-${list}-${i}`}>
            <input value={o.name} onChange={(e) => setOpt(list, i, "name", e.target.value)} className={inputCls} data-testid={`admin-kess3-name-${list}-${i}`} />
            <input type="number" step="0.01" min="0" value={o.price} onChange={(e) => setOpt(list, i, "price", e.target.value)} className={`${inputCls} w-32 shrink-0`} data-testid={`admin-kess3-price-${list}-${i}`} />
            <button type="button" onClick={() => setCfg((c) => ({ ...c, [list]: c[list].filter((_, j) => j !== i) }))} aria-label="Remove option" data-testid={`admin-kess3-remove-${list}-${i}`} className="grid h-10 w-10 shrink-0 place-items-center border border-line text-neutral-400 hover:border-regal-red hover:text-regal-red transition-colors"><Trash2 size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={() => setCfg((c) => ({ ...c, [list]: [...c[list], { name: "", price: 0 }] }))} data-testid={`admin-kess3-add-${list}`} className="inline-flex items-center gap-2 border border-dashed border-linebright px-5 py-2.5 text-sm text-neutral-500 hover:border-regal-amber hover:text-regal-amber transition-colors">
          <Plus size={14} /> Add option
        </button>
      </div>
    </section>
  );

  return (
    <main className="min-h-screen bg-surface pt-28 pb-20" data-testid="admin-kess3-page">
      <div className="container-regal max-w-4xl">
        <Link to="/admin" className="mb-6 inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-regal-amber" data-testid="admin-kess3-back"><ArrowLeft size={15} /> All products</Link>
        <h1 className="mb-2 font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink">KESS3 — Options</h1>
        <p className="mb-8 text-sm text-neutral-400">Hardware price and activation prices used by the KESS3 configurator. All prices in {cfg.currency || "GBP"}. Package conflict rules are fixed — add/remove options carefully.</p>

        <div className="space-y-8">
          <section className="border border-line bg-white p-6 sm:p-8">
            <p className="overline-label mb-6">Hardware</p>
            <label className="block max-w-xs">
              <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">KESS3 hardware price (charged once)</span>
              <input type="number" step="0.01" min="0" value={cfg.hardware_price} onChange={(e) => setCfg({ ...cfg, hardware_price: e.target.value })} className={inputCls} data-testid="admin-kess3-hardware-input" />
            </label>
          </section>
          <Section title="Master activations" list="master_options" />
          <Section title="Slave activations" list="slave_options" />
          <button onClick={save} disabled={busy} data-testid="admin-kess3-save-button" className="bg-regal-amber px-12 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-white hover:bg-amber-400 transition-colors disabled:opacity-60">
            {busy ? "Saving…" : "Save KESS3 Options"}
          </button>
        </div>
      </div>
    </main>
  );
}
